<template>
  <div
    class="image-upload"
    @drop="handleFileDrop"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @dragover="handleDragOver"
  >
    <input
      data-private
      ref="inputFile"
      class="hidden"
      type="file"
      accept="image/*"
      name="image"
      :id="id"
      :disabled="disabled"
      @change="handleInputFileChange"
    />

    <slot
      :selectFile="handleSelectFile"
      :isUploading="isUploading"
      :isDraggingFile="isDraggingFile"
    />
  </div>
</template>

<script>
import get from 'lodash/get'
import uuid from 'uuid/v4'

import ReverMediaSDK from '@/lib/ReverMediaSDK'

export default {
  name: 'ImageUpload',

  props: {
    id: {
      type: String,
      default: uuid(),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isPublic: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data() {
    return {
      imageUploadModalOpen: false,
      isUploading: false,
      isDraggingFile: false,
      imagePreview: {},
      mediaId: null,
      localImageBackground: '',
    }
  },

  methods: {
    handleSelectFile() {
      if (this.disabled) return
      this.$refs.inputFile.click()
    },

    handleInputFileChange(ev) {
      const files = ev.target.files || ev.dataTransfer.files
      this.handleFilesSelect(files)
    },

    handleFilesSelect(files) {
      if (files.length > 0) {
        this.imagePreview = files[0]

        const fileType = this.imagePreview.type
        const imageFormatMatch = /image+/

        if (!imageFormatMatch.test(fileType)) {
          this.$store.dispatch(
            'showError',
            this.$i18n.t('error.attachments.the_file_is_not_supported')
          )
          return false
        }

        return this.onImageCreatedByModal(files[0])
      }
      // Gain focus to the save button
      this.$nextTick(() => {
        const canvasModal = this.$refs.canvasModal
        canvasModal.$el.querySelector('button.btn.btn-primary').focus()
      })
    },

    onImageCreatedByModal(image) {
      this.imageUploadModalOpen = true
      return this.uploadImage(image)
    },

    dataURItoBlob(dataURI) {
      const binary = window.atob(dataURI.split(',')[1])
      const array = []

      for (let i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i))
      }

      return new window.Blob([new Uint8Array(array)], { type: 'image/jpg' })
    },

    uploadImage(file) {
      this.isUploading = true
      return ReverMediaSDK.uploadMedia(file, this.isPublic)
        .then(this.handleSuccessfulUpload)
        .finally(() => (this.isUploading = false))
    },

    async handleSuccessfulUpload(response) {
      if (!response.url) return

      this.doneUploadingImageToServer(response)
    },

    setPreviewImage(file) {
      const reader = new window.FileReader()

      reader.onload = e => {
        this.localImageBackground = e.target.result
      }

      reader.readAsDataURL(file)
    },

    doneUploadingImageToServer(mediaObject) {
      this.$emit('imageUploaded', mediaObject)

      setTimeout(() => {
        this.imageUploadModalOpen = false
      }, 500)
    },

    handleFileDrop(ev) {
      ev.preventDefault()
      ev.dataTransfer.effectAllowed = 'none'

      if (this.disabled) return

      const files = get(ev, 'dataTransfer.files') || []

      this.isDraggingFile = false
      this.handleFilesSelect(files)
    },

    handleDragEnter(ev) {
      ev.preventDefault()
      ev.dataTransfer.effectAllowed = 'none'

      if (this.disabled) return

      this.isDraggingFile = true
    },

    handleDragLeave() {
      if (this.disabled) return

      this.isDraggingFile = false
    },

    handleDragOver(ev) {
      ev.preventDefault()
      ev.dataTransfer.effectAllowed = 'none'
    },
  },
}
</script>

<style lang="scss" scoped>
.image-upload {
  width: 100%;
  height: 100%;
}

.hidden {
  opacity: 0;
  -moz-opacity: 0;
  display: none;
  filter: progid:DXImageTransform.Microsoft.Alpha(opacity=0);
}
</style>
